import {
  AccountDetails,
  ApplicantType,
  DeepPartial,
  ExternalReferenceId,
  ExternalReferenceType,
  Locale,
  ProgramType,
} from 'types';

import { AccountFormSchema } from '../components/Account/AccountForm';

// lseId/tariffId/rate arrive as raw form-input strings (converted via Number() below) even though
// AccountFormSchema — derived from the yup schema's post-validation output — types them as numbers.
type AccountFormInput = Omit<AccountFormSchema, 'lseId' | 'tariffId' | 'rate'> & {
  lseId?: number | string | null;
  tariffId?: number | string | null;
  rate?: number | string | null;
};

export const createAccountRequestBody = (account: AccountFormInput): AccountDetails<any> => {
  const programReferenceId = account.programReferenceId?.trim();
  const externalReferenceIds: ExternalReferenceId[] = programReferenceId
    ? [{ type: ExternalReferenceType.doe, id: programReferenceId }]
    : [];

  const applicants = [
    {
      type: ApplicantType.primary,
      firstName: account.primary.firstName.trim(),
      lastName: account.primary.lastName.trim(),
      middleName: account.primary.middleName?.trim() || undefined,
      phoneNumber: account.primary.phoneNumber,
      email: account.primary.email.trim(),
      address: {
        address1: account.applicantAddress1.trim(),
        address2: account?.applicantAddress2?.trim() || undefined,
        city: account.applicantCity.trim(),
        state: account.applicantState,
        zip: account.applicantZip.trim(),
      },
    },
  ];
  if (account.addAdditionalApplicant === true) {
    applicants.push({
      type: ApplicantType.secondary,
      firstName: account.secondary.firstName.trim(),
      lastName: account.secondary.lastName.trim(),
      middleName: account.secondary.middleName?.trim() || undefined,
      phoneNumber: account.secondary.phoneNumber,
      email: account.secondary.email.trim(),
      address: {
        address1: account.applicantAddress1.trim(),
        address2: account?.applicantAddress2?.trim() || undefined,
        city: account.applicantCity.trim(),
        state: account.applicantState,
        zip: account.applicantZip.trim(),
      },
    });
  }
  return {
    friendlyName: `${account.primary.firstName.trim()}-${account.primary.lastName.trim()}`,
    externalReference: account.reference?.trim() || undefined,
    externalReferenceIds,
    programType: account.programType.trim(),
    address: {
      address1: account.address1.trim(),
      address2: account.address2?.trim() || undefined,
      city: account.city.trim(),
      state: account.state,
      zip: account.zip.trim(),
    },
    applicants,
    ...(account.lat &&
      account.lon && {
        coordinates: {
          lat: account.lat,
          lon: account.lon,
        },
      }),
    language: account.language || Locale.en,
    ...((account.programType === ProgramType.hvac || account.programType === ProgramType.waterHeater) && {
      propertyDetails: {
        homeSize: account.homeSize,
        existingSystems: account.existingSystems,
        systemsBeingReplaced: account.systemsBeingReplaced,
      },
    }),
    ...(account.programType === ProgramType.geothermal && {
      propertyDetails: {
        homeSize: account.homeSize,
      },
    }),
    ...(account.programType === ProgramType.generator && {
      propertyDetails: {
        numberOfSystems: account.numberOfSystems,
      },
    }),
    ...(account.lseId && {
      utility: {
        lseId: Number(account.lseId),
        tariffId: account.tariffId ? Number(account.tariffId) : undefined,
        rate: account.rate ? Number(account.rate) : undefined,
      },
    }),
    salesRepName: account.salesRepName?.trim() || undefined,
    salesRepLicenseNumber: account.salesRepLicenseNumber || undefined,
    salesRepEmail: account.salesRepEmail?.trim()?.toLocaleLowerCase() || undefined,
    salesRepPhoneNumber: account.salesRepPhoneNumber?.trim() || undefined,
  } as AccountDetails<any>;
};

export const filterOutDisabledFields = (account: any, disabledFields: string[]): any => {
  const keys = Object.keys(account);
  const filteredKeys = keys.filter((key) => !disabledFields.includes(key));
  const filteredObject = filteredKeys.reduce((obj, key) => {
    return {
      ...obj,
      [key]: account[key as keyof AccountFormSchema],
    };
  }, {});
  return filteredObject;
};

export const createEditAccountRequestBody = (
  account: AccountFormInput & { hasAdditionalApplicant: boolean },
): DeepPartial<AccountDetails<any>> => {
  const friendlyName =
    account.primary.firstName && account.primary.lastName
      ? `${account.primary.firstName.trim()}-${account.primary.lastName.trim()}`
      : undefined;
  const externalReference = account.reference?.trim();
  const programReferenceId = account.programReferenceId?.trim();
  const externalReferenceIds: ExternalReferenceId[] = programReferenceId
    ? [{ type: ExternalReferenceType.doe, id: programReferenceId }]
    : [];

  const programType = account.programType?.trim();
  const address = {
    address1: account.address1?.trim(),
    address2: account.address2?.trim(),
    city: account.city?.trim(),
    state: account?.state,
    zip: account.zip?.trim(),
  };
  const applicantAddress = {
    address1: account.applicantAddress1?.trim(),
    address2: account?.applicantAddress2?.trim(),
    city: account.applicantCity?.trim(),
    state: account.applicantState,
    zip: account.applicantZip?.trim(),
  };
  const applicants = [
    {
      type: ApplicantType.primary,
      phoneNumber: account.primary.phoneNumber,
      email: account.primary.email?.trim()?.toLowerCase(),
      firstName: account.primary.firstName?.trim(),
      lastName: account.primary.lastName?.trim(),
      middleName: account.primary.middleName?.trim(),
      ...(Object.values(applicantAddress).some((val) => val !== undefined) && { address: applicantAddress }),
    },
  ];
  if (account.addAdditionalApplicant) {
    applicants.push({
      type: ApplicantType.secondary,
      phoneNumber: account.secondary.phoneNumber,
      email: account.secondary.email?.trim()?.toLowerCase(),
      firstName: account.secondary.firstName?.trim(),
      lastName: account.secondary.lastName?.trim(),
      middleName: account.secondary.middleName?.trim(),
      ...(Object.values(applicantAddress).some((val) => val !== undefined) && { address: applicantAddress }),
    });
  }
  const coordinates = {
    lat: account.lat,
    lon: account.lon,
  };
  const propertyDetails = {
    homeSize: account.homeSize,
    existingSystems: account.existingSystems,
    systemsBeingReplaced: account.systemsBeingReplaced,
  };
  const utility = {
    lseId: account.lseId ? Number(account.lseId) : undefined,
    tariffId: account.tariffId ? Number(account.tariffId) : undefined,
    rate: account.rate ? Number(account.rate) : undefined,
  };
  return {
    friendlyName,
    externalReference,
    externalReferenceIds,
    programType,
    language: account.language,
    ...(Object.values(address).some((val) => val !== undefined) && { address }),
    applicants,
    ...(Object.values(coordinates).some((val) => val !== undefined) && { coordinates }),
    ...(Object.values(propertyDetails).some((val) => val !== undefined) && { propertyDetails }),
    ...(Object.values(utility).some((val) => val !== undefined && val !== null) && { utility }),
    salesRepName: account.salesRepName?.trim() || undefined,
    salesRepLicenseNumber: account.salesRepLicenseNumber,
    salesRepEmail: account.salesRepEmail?.trim()?.toLocaleLowerCase() || undefined,
    salesRepPhoneNumber: account.salesRepPhoneNumber?.trim(),
  };
};
